import { useStore } from '@tanstack/react-form'
import { useMutation } from '@tanstack/react-query'
import { GetLakehouseTablesDocument } from '@vendia/control-plane-api'
import { LakehouseConnectionTypeEnum, LakehouseDatabricksConnectionInput } from '@vendia/management-api-types'
import debug from 'debug'
import { useNavigate } from 'react-router'
import Card from 'src/components/containers/card'
import ListboxField from 'src/components/fields/listbox.field'
import TextField from 'src/components/fields/text.field'
import { ScrollableStepContent } from 'src/components/flows/scrollable-step-content'
import StepButtons from 'src/components/flows/step-buttons'
import { StepContentHeader } from 'src/components/flows/step-header'
import { StepWrapper } from 'src/components/flows/step-wrapper'
import { StepComponent } from 'src/components/flows/types'
import { createOnChangeValidator, isRequiredValidator } from 'src/utils/form/validation'
import useControlPlaneApi from 'src/utils/hooks/use-control-plane-api'
import { useFirstFocus } from 'src/utils/hooks/use-first-focus'
import { LakehouseFlowStepValues } from 'src/utils/lakehouse/types'
import { lakehouseFlowFieldValidator } from 'src/utils/lakehouse/validators'
import notify from 'src/utils/notify'

import { StepId } from '../config'

const logger = debug('app:DataBricks')

export const StepConnectDatabricks: StepComponent<LakehouseFlowStepValues> = ({ context }) => {
  const form = context.form
  const client = useControlPlaneApi()
  const navigate = useNavigate()
  const prefix = 'source.databricks'
  const nameRef = useFirstFocus()

  const { uniName, nodeName } = useStore(form.store, (s) => s.values)
  const connection = useStore(form.store, (s) => s.values.source?.databricks)
  const connectionType = useStore(form.store, (s) => s.values.source?.connection)

  const getTablesMutation = useMutation({
    mutationFn: () =>
      client.graphqlFetch({
        query: GetLakehouseTablesDocument,
        variables: {
          uniName: uniName!,
          nodeName: nodeName!,
          connectionInput: {
            type: LakehouseConnectionTypeEnum.Databricks,
            databricksConnection: {
              ...(connection as LakehouseDatabricksConnectionInput),
            },
          },
        },
      }),
    onError: (error) => notify.error(`Error testing Databricks connection: ${error}`),
    onSuccess: (response) => {
      logger('databricks connection response', response)
      if (response.errors) {
        notify.error(`${response.errors[0].message}`)
        return
      }
      if (!response.getLakehouseTables) {
        notify.error('Error testing Databricks connection')
        return
      }
      form.setFieldValue('source.availableTables', response.getLakehouseTables.tables as string[])
      form.setFieldValue('source.sourceRegion', response.getLakehouseTables.region as string)
      context.goToStep(StepId.SelectTable)
    },
  })

  return (
    <StepWrapper>
      <StepContentHeader
        centered
        large
        title='Connect to Databricks'
        description="Let's authenticate your Databricks database"
      />
      <ScrollableStepContent inset autoHeight>
        <Card padding='xl' className='mx-8 w-full max-w-4xl'>
          <TextField
            ref={nameRef}
            name='connectionName'
            label='Name'
            description='Easily identify and reuse this connection to ingest additional tables.'
            form={form}
            useNestedLabel
            maxLength={64}
            validators={createOnChangeValidator(
              {
                pattern: /^(?!\s*$).{1,64}$/,
                message: 'The connection name must be between 1 and 64 characters long.',
              },
              true,
            )}
          />

          <div className='flex w-full flex-col gap-4'>
            <TextField
              name={`${prefix}.host`}
              label='Host'
              description='The Host of your Databricks server'
              form={form}
              useNestedLabel
              validators={lakehouseFlowFieldValidator({
                stepId: StepId.ConnectDatabricks,
                listenTo: ['source.connection'],
                validator: isRequiredValidator(null, connectionType === LakehouseConnectionTypeEnum.Databricks),
              })}
            />
            <TextField
              name={`${prefix}.httpPath`}
              label='httpPath'
              description='The HttpPath of your Databricks server'
              form={form}
              useNestedLabel
              validators={lakehouseFlowFieldValidator({
                stepId: StepId.ConnectDatabricks,
                listenTo: ['source.connection'],
                validator: isRequiredValidator(null, connectionType === LakehouseConnectionTypeEnum.Databricks),
              })}
            />
            <TextField
              name={`${prefix}.catalog`}
              label='Catalog'
              description='The Catalog of your Databricks server'
              form={form}
              useNestedLabel
              validators={lakehouseFlowFieldValidator({
                stepId: StepId.ConnectDatabricks,
                listenTo: ['source.connection'],
                validator: isRequiredValidator(null, connectionType === LakehouseConnectionTypeEnum.Databricks),
              })}
            />
            <TextField
              name={`${prefix}.schema`}
              label='Schema'
              description='The Schema of your Databricks server'
              form={form}
              useNestedLabel
              validators={lakehouseFlowFieldValidator({
                stepId: StepId.ConnectDatabricks,
                listenTo: ['source.connection'],
                validator: isRequiredValidator(null, connectionType === LakehouseConnectionTypeEnum.Databricks),
              })}
            />
            <TextField
              name={`${prefix}.clientId`}
              label='Client ID'
              description='The Client Id of your Databricks server'
              form={form}
              useNestedLabel
              validators={lakehouseFlowFieldValidator({
                stepId: StepId.ConnectDatabricks,
                listenTo: ['source.connection'],
                validator: isRequiredValidator(null, connectionType === LakehouseConnectionTypeEnum.Databricks),
              })}
            />
            <TextField
              name={`${prefix}.clientSecret`}
              label='Client Secret'
              description='The Client Secret of your Databricks server'
              form={form}
              useNestedLabel
              validators={lakehouseFlowFieldValidator({
                stepId: StepId.ConnectDatabricks,
                listenTo: ['source.connection'],
                validator: isRequiredValidator(null, connectionType === LakehouseConnectionTypeEnum.Databricks),
              })}
            />
            <TextField
              name={`${prefix}.oauthServer`}
              label='oAuth Server URL'
              description='The oAuth Server URL of your Databricks server'
              form={form}
              useNestedLabel
              validators={lakehouseFlowFieldValidator({
                stepId: StepId.ConnectDatabricks,
                listenTo: ['source.connection'],
                validator: isRequiredValidator(null, connectionType === LakehouseConnectionTypeEnum.Databricks),
              })}
            />
          </div>
        </Card>
      </ScrollableStepContent>
      <form.Subscribe selector={(state) => [state.canSubmit, state.isPristine]}>
        {([canSubmit, isPristine]) => (
          <StepButtons
            context={context}
            isSubmitting={getTablesMutation.isPending}
            nextBlocked={getTablesMutation.isPending || !canSubmit || isPristine}
            hasCancel={true}
            onCancel={() => navigate('../', { relative: 'path' })}
            onNext={() => getTablesMutation.mutate()}
          />
        )}
      </form.Subscribe>
    </StepWrapper>
  )
}
